/**
 * Worker integration for Trimline mesh cutting operations
 * Offloads heavy CSG operations to worker threads for smooth UI
 */
import * as THREE from 'three';
import { geometryWorkerPool } from '../../../utils/WorkerPool.js';

/**
 * Perform mesh cutting operation in worker thread
 * @param {Object} params - Parameters for cutting
 * @param {Float32Array} params.positions - Mesh vertex positions
 * @param {Uint32Array} params.indices - Mesh face indices
 * @param {Array} params.curvePoints - Curve boundary points {x, y, z}
 * @param {Array} params.curveNormals - Curve normals (optional)
 * @param {boolean} params.keepBothParts - Whether to return both cut parts
 * @param {Object} params.matrixWorld - Mesh world transformation matrix
 * @param {AbortSignal} signal - Abort signal for cancellation
 * @returns {Promise<Object>} - Cut geometry data
 */
export async function cutMeshByLoopAsync(params, signal = null) {
  // Check if operation was cancelled before starting
  if (signal?.aborted) {
    throw new DOMException('Operation cancelled', 'AbortError');
  }

  try {
    // Execute in worker pool - pass arguments spread out, signal as last arg
    // Following the same pattern as CarveReliefWorkerIntegration
    const result = await geometryWorkerPool.execute(
      'cutMeshByLoop',
      params.positions,
      params.indices,
      params.colors || null,
      params.curvePoints,
      params.curveNormals || null,
      params.keepBothParts || false,
      params.matrixWorld,
      signal // Signal as last argument
    );

    // Check if cancelled during execution
    if (signal?.aborted) {
      throw new DOMException('Operation cancelled', 'AbortError');
    }

    return result;
  } catch (error) {
    if (error.name === 'AbortError' || error.message?.includes('cancelled')) {
      console.log('[TrimlineWorker] Operation cancelled');
      throw error;
    }
    console.error('[TrimlineWorker] Cut mesh operation failed:', error);
    throw error;
  }
}

/**
 * Convert worker result back to THREE.BufferGeometry
 * @param {Object} workerResult - Result from worker
 * @returns {THREE.BufferGeometry} - Reconstructed geometry
 */
export function reconstructGeometryFromWorkerResult(workerResult) {
  if (!workerResult || !workerResult.positions) {
    console.warn('[TrimlineWorker] Invalid worker result, cannot reconstruct geometry');
    return null;
  }

  const geometry = new THREE.BufferGeometry();

  // Set position attribute
  geometry.setAttribute(
    'position',
    new THREE.BufferAttribute(new Float32Array(workerResult.positions), 3)
  );

  // Set index if available
  if (workerResult.indices) {
    geometry.setIndex(new THREE.BufferAttribute(new Uint32Array(workerResult.indices), 1));
  }

  // Set color attribute if available
  if (workerResult.colors && workerResult.colors.length > 0) {
    geometry.setAttribute(
      'color',
      new THREE.BufferAttribute(new Float32Array(workerResult.colors), 3)
    );
  }

  // Set normal attribute if available
  if (workerResult.normals) {
    geometry.setAttribute(
      'normal',
      new THREE.BufferAttribute(new Float32Array(workerResult.normals), 3)
    );
  } else {
    // Compute normals if not provided
    geometry.computeVertexNormals();
  }

  return geometry;
}

/**
 * Prepare mesh data for worker transfer
 * @param {THREE.Mesh} mesh - Source mesh
 * @returns {Object} - Serializable mesh data
 */
export function prepareMeshDataForWorker(mesh) {
  const geometry = mesh.geometry;
  const positions = geometry.attributes.position.array;
  const indices = geometry.index ? geometry.index.array : null;

  // Extract color attribute if it exists
  const colors = geometry.attributes.color ? geometry.attributes.color.array : null;

  // Extract world matrix as plain array for serialization
  const matrixWorld = {
    elements: Array.from(mesh.matrixWorld.elements)
  };

  return {
    positions: positions,
    indices: indices,
    colors: colors,
    matrixWorld: matrixWorld
  };
}
